<template>
  <v-card class="v3nav-editor">
    <v-card-title class="d-flex align-center">
      <span>V3 Navigation</span>
      <v-spacer></v-spacer>
      <v-btn small outlined class="mr-2" @click="newGroupDialog = true">
        <v-icon small left>mdi-folder-plus-outline</v-icon>New Group
      </v-btn>
      <v-btn small outlined class="mr-2" @click="confirmReset">
        <v-icon small left>mdi-restore</v-icon>Reset to defaults
      </v-btn>
      <v-btn small color="primary" class="mr-2" :loading="saving" @click="save">Save</v-btn>
      <v-btn icon small @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>

    <v-card-text>
      <div class="v3nav-hint">
        Drag top-level items to reorder the navigation rail; drag pages between
        areas (or into <b>Hidden</b>) to change where they appear in the
        contextual "On this page" list. Use <b>New Group</b> to add a container
        with no page of its own. Labels and icons are edited on the Meta Admin
        grid. Saved changes apply for everyone on next sign-in / refresh.
      </div>

      <div class="v3nav-cols">
        <!-- Rail (tree) -->
        <div class="v3nav-rail">
          <div class="v3nav-area v3nav-area--locked">
            <div class="v3nav-area-head">
              <v-icon small class="mr-1" style="opacity: 0.35">mdi-lock-outline</v-icon>
              <b>Home</b>
              <span class="v3nav-sub ml-2">pinned — targets the customer's default landing page</span>
            </div>
          </div>

          <draggable v-model="areas" handle=".v3nav-grip" animation="150">
            <div v-for="a in areas" :key="a.key" class="v3nav-area">
              <div class="v3nav-area-head">
                <v-icon small class="v3nav-grip">mdi-drag</v-icon>
                <b>{{ a.label }}</b>
                <v-chip v-if="a.kind === 'group'" x-small class="ml-2" outlined>group</v-chip>
                <v-chip v-if="a.custom" x-small class="ml-1" color="primary" outlined>custom</v-chip>
                <span v-if="a.kind === 'route'" class="v3nav-sub ml-2">{{ a.key }}</span>
                <v-spacer></v-spacer>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item v-if="a.custom" @click="renameGroup(a)">
                      <v-list-item-title>Rename</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="a.custom" @click="deleteGroup(a)">
                      <v-list-item-title>Delete group</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="a.kind === 'route'"
                      :disabled="a.children.length > 0"
                      @click="demoteArea(a)"
                    >
                      <v-list-item-title>Remove from rail (hide)</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <draggable
                v-model="a.children"
                :group="{ name: 'toc' }"
                class="v3nav-children"
                animation="150"
              >
                <div v-for="c in a.children" :key="c.name" class="v3nav-child">
                  <v-icon x-small class="v3nav-grip-c">mdi-drag</v-icon>
                  <span class="v3nav-child-lbl">{{ c.label }}</span>
                  <span class="v3nav-sub">{{ c.name }}</span>
                  <v-chip v-if="c.dynamic" x-small class="ml-1" outlined>dynamic</v-chip>
                  <v-spacer></v-spacer>
                  <v-btn
                    v-if="!isArea(c.name)"
                    icon
                    x-small
                    title="Make top-level (rail item)"
                    @click="promoteChild(a, c)"
                  >
                    <v-icon small>mdi-arrow-collapse-up</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    title="Remove from navigation (moves to Hidden)"
                    @click="hideChild(a, c)"
                  >
                    <v-icon small>mdi-eye-off-outline</v-icon>
                  </v-btn>
                </div>
              </draggable>
            </div>
          </draggable>
        </div>

        <!-- Hidden tray -->
        <div class="v3nav-hiddencol">
          <div class="v3nav-area-head">
            <v-icon small class="mr-1">mdi-eye-off-outline</v-icon>
            <b>Hidden</b>
            <span class="v3nav-sub ml-2">not shown in the v3 nav</span>
          </div>
          <draggable
            v-model="hidden"
            :group="{ name: 'toc' }"
            class="v3nav-children v3nav-children--hidden"
            animation="150"
          >
            <div v-for="c in hidden" :key="c.name" class="v3nav-child">
              <v-icon x-small class="v3nav-grip-c">mdi-drag</v-icon>
              <span class="v3nav-child-lbl">{{ c.label }}</span>
              <span class="v3nav-sub">{{ c.name }}</span>
              <v-chip v-if="c.dynamic" x-small class="ml-1" outlined>dynamic</v-chip>
              <v-spacer></v-spacer>
              <v-btn
                v-if="!isArea(c.name)"
                icon
                x-small
                title="Make top-level (rail item)"
                @click="promoteChild(null, c)"
              >
                <v-icon small>mdi-arrow-collapse-up</v-icon>
              </v-btn>
            </div>
          </draggable>
        </div>
      </div>
    </v-card-text>

    <!-- New / rename group dialog -->
    <v-dialog v-model="newGroupDialog" max-width="420">
      <v-card class="pa-4">
        <h3 class="mb-3">{{ editingGroup ? 'Rename group' : 'New group' }}</h3>
        <v-text-field v-model="groupForm.label" label="Label" dense autofocus></v-text-field>
        <v-text-field
          v-if="!editingGroup"
          v-model="groupForm.icon"
          label="Icon (Material Symbols name)"
          dense
          placeholder="folder"
        ></v-text-field>
        <div class="d-flex">
          <v-spacer></v-spacer>
          <v-btn text small class="mr-2" @click="closeGroupDialog">Cancel</v-btn>
          <v-btn small color="primary" :disabled="!groupForm.label" @click="applyGroupDialog">OK</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <ResponseHandler :serviceResponse="response"></ResponseHandler>
  </v-card>
</template>

<script>
import axios from "axios";
import draggable from "vuedraggable";
import ResponseHandler from "@/components/ResponseHandler";

/**
 * V3 Navigation editor — a drag-and-drop tree over the same per-customer
 * route_meta_override rows the Meta Admin grid edits, but scoped to nav
 * PLACEMENT: v3.rail / v3.parent / v3.order per route, plus synthetic group
 * rows (route_name `v3group.<key>`, meta_key text|icon|rail). Labels/icons of
 * pages stay on the grid (v3.tocText / v3.icon).
 *
 * The tree is built from the code defaults (meta.Default.v3, v3GroupsDefault)
 * with the saved overrides applied, and saving writes the MINIMAL diff back:
 * areas whose membership + ordering match the defaults produce no rows, and
 * rows no longer needed are deleted (restoring the defaults).
 */
export default {
  name: "V3NavEditor",
  components: { draggable, ResponseHandler },
  props: {
    savedOverrides: { type: Array, default: () => [] },
  },
  data() {
    return {
      areas: [],
      hidden: [],
      editorRoutes: [],
      saving: false,
      response: null,
      newGroupDialog: false,
      editingGroup: null,
      groupForm: { label: "", icon: "folder" },
    };
  },
  created() {
    this.buildModel();
  },
  methods: {
    // ---- model build -------------------------------------------------------
    defaultV3(r) {
      const meta = r.meta || {};
      const d = meta.Default && "v3" in meta.Default ? meta.Default.v3 : meta.v3;
      return d || null;
    },
    overrideValue(name, prop) {
      const o = this.savedOverrides.find(
        (x) => x.route_name === name && x.meta_key === "v3." + prop
      );
      return o ? o.meta_value : undefined;
    },
    buildModel() {
      const clientSettings = this.$loginState.user.settings || [];

      // Groups: code defaults + v3group.* override rows (per-customer).
      const groups = {};
      (this.$router.v3GroupsDefault || this.$router.v3Groups || []).forEach((g) => {
        groups[g.key] = { ...g, custom: false };
      });
      this.savedOverrides
        .filter((o) => String(o.route_name).startsWith("v3group."))
        .forEach((o) => {
          const key = String(o.route_name).slice("v3group.".length);
          if (!key) return;
          if (!groups[key]) groups[key] = { key, text: key, icon: "folder", rail: 100, custom: true };
          if (o.meta_key === "rail") {
            const n = Number(o.meta_value);
            if (!isNaN(n)) groups[key].rail = n;
          } else if (o.meta_key === "text" || o.meta_key === "icon") {
            groups[key][o.meta_key] = o.meta_value;
          }
        });

      // Routes visible to this customer (incl. dynamic dashboards / landing
      // pages, which the login flow registers before this page can open).
      this.editorRoutes = this.$router
        .getRoutes()
        .filter(
          (r) =>
            r.name &&
            r.name !== "home" &&
            r.meta &&
            r.meta.newUi &&
            !r.meta.disabled &&
            !r.meta.internal &&
            (r.meta.module_setting
              ? clientSettings.find(
                  (x) => x.setting === r.meta.module_setting && x.value === "true"
                )
              : true)
        )
        .map((r) => {
          const def = this.defaultV3(r) || {};
          const eff = { ...def };
          ["rail", "parent", "order", "tocText"].forEach((p) => {
            const v = this.overrideValue(r.name, p);
            if (v === undefined) return;
            if (v === "" || v === null || v === "none") delete eff[p];
            else if (p === "rail" || p === "order") {
              const n = Number(v);
              if (!isNaN(n)) eff[p] = n;
            } else eff[p] = v;
          });
          return {
            name: r.name,
            text: (r.meta && r.meta.text) || r.name,
            def,
            eff,
            dynamic: !!(r.meta.dashboardId || r.meta.landingPageId),
          };
        });

      // Areas = rail routes + groups, by effective rail position.
      const areas = [];
      this.editorRoutes
        .filter((rt) => rt.eff.rail != null)
        .forEach((rt) =>
          areas.push({
            kind: "route",
            key: rt.name,
            label: rt.text,
            rail: rt.eff.rail,
            custom: false,
            children: [],
          })
        );
      Object.values(groups).forEach((g) =>
        areas.push({
          kind: "group",
          key: g.key,
          label: g.text || g.key,
          icon: g.icon,
          rail: g.rail != null ? g.rail : 100,
          custom: !!g.custom,
          children: [],
        })
      );
      areas.sort((a, b) => a.rail - b.rail);

      const areaKeys = new Set(areas.map((a) => a.key));
      const childItem = (rt) => ({
        name: rt.name,
        label: rt.eff.tocText || rt.text,
        dynamic: rt.dynamic,
      });

      this.editorRoutes
        .filter((rt) => rt.eff.parent && areaKeys.has(rt.eff.parent))
        .sort(
          (a, b) =>
            (a.eff.order != null ? a.eff.order : 100) -
              (b.eff.order != null ? b.eff.order : 100) ||
            String(a.eff.tocText || a.text).localeCompare(String(b.eff.tocText || b.text))
        )
        .forEach((rt) => {
          const area = areas.find((a) => a.key === rt.eff.parent);
          area.children.push(childItem(rt));
        });

      this.hidden = this.editorRoutes
        .filter(
          (rt) => rt.eff.rail == null && !(rt.eff.parent && areaKeys.has(rt.eff.parent))
        )
        .sort((a, b) => a.text.localeCompare(b.text))
        .map(childItem);

      this.areas = areas;
    },

    // ---- tree actions ------------------------------------------------------
    isArea(name) {
      return this.areas.some((a) => a.kind === "route" && a.key === name);
    },
    promoteChild(fromArea, child) {
      if (this.isArea(child.name)) return;
      if (fromArea) fromArea.children = fromArea.children.filter((c) => c.name !== child.name);
      else this.hidden = this.hidden.filter((c) => c.name !== child.name);
      const rt = this.editorRoutes.find((r) => r.name === child.name);
      this.areas.push({
        kind: "route",
        key: child.name,
        label: rt ? rt.text : child.label,
        rail: 999,
        custom: false,
        children: [],
      });
    },
    // Remove a page from this area's ToC. Goes to the Hidden tray — unless the
    // route is itself a rail area (a self-entry like All Jobs' first ToC item),
    // in which case it just leaves the ToC but stays on the rail.
    hideChild(area, child) {
      area.children = area.children.filter((c) => c.name !== child.name);
      if (!this.isArea(child.name)) this.hidden.push(child);
    },
    demoteArea(area) {
      if (area.kind !== "route" || area.children.length) return;
      this.areas = this.areas.filter((a) => a !== area);
      const rt = this.editorRoutes.find((r) => r.name === area.key);
      this.hidden.push({ name: area.key, label: rt ? rt.text : area.label, dynamic: rt ? rt.dynamic : false });
    },
    renameGroup(area) {
      this.editingGroup = area;
      this.groupForm = { label: area.label, icon: area.icon || "folder" };
      this.newGroupDialog = true;
    },
    deleteGroup(area) {
      if (!area.custom) return;
      this.hidden.push(...area.children);
      this.areas = this.areas.filter((a) => a !== area);
    },
    closeGroupDialog() {
      this.newGroupDialog = false;
      this.editingGroup = null;
      this.groupForm = { label: "", icon: "folder" };
    },
    applyGroupDialog() {
      if (this.editingGroup) {
        this.editingGroup.label = this.groupForm.label;
      } else {
        let key = String(this.groupForm.label)
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
        if (!key) key = "group";
        const taken = (k) =>
          this.areas.some((a) => a.key === k) || this.editorRoutes.some((r) => r.name === k);
        let unique = key;
        let i = 2;
        while (taken(unique)) unique = key + i++;
        this.areas.push({
          kind: "group",
          key: unique,
          label: this.groupForm.label,
          icon: this.groupForm.icon || "folder",
          rail: 999,
          custom: true,
          children: [],
        });
      }
      this.closeGroupDialog();
    },
    confirmReset() {
      if (!confirm("Remove ALL v3 navigation placement overrides for this customer and restore the defaults?")) return;
      this.persist({});
    },

    // ---- save --------------------------------------------------------------
    // Desired placement rows, minimal against the code defaults.
    computeDesired() {
      const desired = {}; // `${route_name}|${meta_key}` -> value

      // Rail sequence: only write rail rows when it differs from the default.
      const defaultRail = [
        ...this.editorRoutes
          .filter((rt) => rt.def.rail != null)
          .map((rt) => ({ key: rt.name, rail: rt.def.rail })),
        ...(this.$router.v3GroupsDefault || []).map((g) => ({
          key: g.key,
          rail: g.rail != null ? g.rail : 100,
        })),
      ]
        .sort((a, b) => a.rail - b.rail)
        .map((x) => x.key);
      const desiredRail = this.areas.map((a) => a.key);

      if (JSON.stringify(defaultRail) !== JSON.stringify(desiredRail)) {
        this.areas.forEach((a, i) => {
          const rail = String(i + 1);
          if (a.kind === "route") desired[`${a.key}|v3.rail`] = rail;
          else desired[`v3group.${a.key}|rail`] = rail;
        });
      }

      // Custom groups always need their definition rows.
      this.areas
        .filter((a) => a.kind === "group" && a.custom)
        .forEach((a) => {
          desired[`v3group.${a.key}|text`] = a.label;
          desired[`v3group.${a.key}|icon`] = a.icon || "folder";
        });
      // Static groups renamed? (rename UI only targets custom groups today,
      // but a text row may exist from the grid — leave those alone.)

      // Per-area children: rows only when membership/ordering changed.
      this.areas.forEach((a) => {
        const defaultChildren = this.editorRoutes
          .filter((rt) => rt.def.parent === a.key)
          .sort(
            (x, y) =>
              (x.def.order != null ? x.def.order : 100) -
                (y.def.order != null ? y.def.order : 100) ||
              String(x.def.tocText || x.text).localeCompare(String(y.def.tocText || y.text))
          )
          .map((rt) => rt.name);
        const desiredChildren = a.children.map((c) => c.name);
        if (JSON.stringify(defaultChildren) === JSON.stringify(desiredChildren)) return;

        a.children.forEach((c, i) => {
          const rt = this.editorRoutes.find((r) => r.name === c.name);
          if (!rt) return;
          if (rt.def.parent !== a.key) desired[`${c.name}|v3.parent`] = a.key;
          desired[`${c.name}|v3.order`] = String(i + 1);
        });
      });

      // Hidden: 'none' rows only where the default placed the route somewhere.
      const inAnyChildren = new Set();
      this.areas.forEach((a) => a.children.forEach((c) => inAnyChildren.add(c.name)));
      const areaRouteKeys = new Set(
        this.areas.filter((a) => a.kind === "route").map((a) => a.key)
      );
      this.editorRoutes.forEach((rt) => {
        if (rt.def.rail != null && !areaRouteKeys.has(rt.name))
          desired[`${rt.name}|v3.rail`] = "none";
        if (rt.def.parent && !inAnyChildren.has(rt.name))
          desired[`${rt.name}|v3.parent`] = "none";
      });

      return desired;
    },
    isPlacementRow(o) {
      return (
        ["v3.rail", "v3.parent", "v3.order"].includes(o.meta_key) ||
        String(o.route_name).startsWith("v3group.")
      );
    },
    async persist(desired) {
      this.saving = true;
      try {
        const upserts = [];
        Object.entries(desired).forEach(([k, value]) => {
          const [route_name, meta_key] = k.split("|");
          const existing = this.savedOverrides.find(
            (o) => o.route_name === route_name && o.meta_key === meta_key
          );
          if (!existing || existing.meta_value !== value)
            upserts.push({ route_name, meta_key, meta_value: value });
        });
        const deletes = this.savedOverrides.filter(
          (o) => this.isPlacementRow(o) && desired[`${o.route_name}|${o.meta_key}`] === undefined
        );

        if (upserts.length) {
          const resp = await axios.post("admin/routeMetaOverrideSave", { overrides: upserts });
          this.response = resp.data;
          if (resp.data.Status !== "OK") return;
        }
        for (const d of deletes) {
          const resp = await axios.post("admin/routeMetaOverrideDelete", {
            routeName: d.route_name,
            metaKey: d.meta_key,
          });
          if (resp.data.Status !== "OK") {
            this.response = resp.data;
            return;
          }
        }

        // New saved set = old non-placement rows + desired placement rows.
        const updated = this.savedOverrides.filter((o) => !this.isPlacementRow(o));
        Object.entries(desired).forEach(([k, value]) => {
          const [route_name, meta_key] = k.split("|");
          updated.push({ route_name, meta_key, meta_value: value });
        });
        this.response = { Status: "OK", Message: "V3 navigation saved — applies on next sign-in / refresh" };
        this.$emit("saved", updated);
      } catch (err) {
        if (err.response && err.response.status === 401) this.$emit("sessionExpired", err);
        else alert(err.response ? err.response.data.message : err);
        console.log(err);
      } finally {
        this.saving = false;
      }
    },
    save() {
      this.persist(this.computeDesired());
    },
  },
};
</script>

<style scoped lang="scss">
.v3nav-editor {
  .v3nav-hint {
    font-size: 12px;
    color: #777;
    line-height: 1.5;
    margin-bottom: 14px;
  }

  .v3nav-cols {
    display: flex;
    gap: 20px;
    align-items: flex-start;
  }

  .v3nav-rail { flex: 1 1 60%; min-width: 0; }
  .v3nav-hiddencol { flex: 1 1 40%; min-width: 0; position: sticky; top: 0; }

  .v3nav-area {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    margin-bottom: 8px;
    background: rgba(0, 0, 0, 0.015);

    &--locked { opacity: 0.7; }
  }

  .v3nav-area-head {
    display: flex;
    align-items: center;
    padding: 8px 10px;
    font-size: 13.5px;
  }

  .v3nav-grip,
  .v3nav-grip-c { cursor: grab; margin-right: 6px; opacity: 0.5; }

  .v3nav-children {
    min-height: 6px;
    padding: 0 10px 8px 28px;

    &--hidden {
      min-height: 200px;
      border: 1px dashed rgba(0, 0, 0, 0.15);
      border-radius: 8px;
      padding: 8px 10px;
      margin-top: 6px;
    }
  }

  .v3nav-child {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    margin-bottom: 3px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    background: #fff;
    font-size: 13px;
  }

  .v3nav-child-lbl { font-weight: 500; margin-right: 8px; }

  .v3nav-sub { font-size: 11px; color: #999; }
}
</style>
